import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import { isExternalCheckoutUrl } from '../utils/checkoutUrl'
import type { SetupFee_CheckoutResponseDados } from '../contracts/SetupFee_CheckoutResponse'

// Side-effect imports: registers the contracts with the validation registry.
import '../contracts/SetupFee_CheckoutResponse'

/**
 * useSetupFeeCheckout — SOLE network owner for GET subscriptions/setup-fee/checkout/{id},
 * the franqueado setup-fee checkout door.
 *
 * All requests go to `config.api.sistemaHost` (app-api-service).
 */

// ── Module-scope singleton state ────────────────────────────────────────────

const checkingOutFranquiaId = ref<number | null>(null)
const checkoutError = ref<string | null>(null)

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

export function useSetupFeeCheckout() {
  /**
   * redirectToCheckout — thin, spy-able seam for external payment page redirect.
   * Keeps .vue free of window.*.
   */
  function redirectToCheckout(url: string): void {
    log('useSetupFeeCheckout', 'redirectToCheckout', 'Redirecionando para checkout de setup', { url })
    // New tab, always: the SPA stays alive and the user never needs the
    // browser back button to return from the gateway (2026-08-20).
    window.open(url, '_blank', 'noopener')
  }

  /**
   * fetchCheckout — GET subscriptions/setup-fee/checkout/{id}.
   */
  async function fetchCheckout(franquiaId: number): Promise<SetupFee_CheckoutResponseDados> {
    checkingOutFranquiaId.value = franquiaId
    checkoutError.value = null

    try {
      const raw = await httpService.get(
        sistemaUrl(`subscriptions/setup-fee/checkout/${franquiaId}`),
        {
          responseContract: 'SetupFee_CheckoutResponse',
          skipErrorDisplay: true,
          throwOnEnvelopeFailure: 'setup_fee_checkout_failed',
        },
      )

      const resp = raw as { dados: SetupFee_CheckoutResponseDados }

      log('useSetupFeeCheckout', 'fetchCheckout', 'Checkout de setup obtido', {
        franquia_id: franquiaId,
        charge_id: resp.dados?.charge_id,
        amount_cents: resp.dados?.amount_cents,
        currency: resp.dados?.currency,
        status: resp.dados?.status,
      })

      return resp.dados
    } catch (err) {
      checkoutError.value = (err as Error)?.message ?? 'setup_fee_checkout_failed'
      log('useSetupFeeCheckout', 'fetchCheckout', 'Erro ao obter checkout de setup', {
        franquia_id: franquiaId,
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      checkingOutFranquiaId.value = null
    }
  }

  /**
   * checkoutAndRedirect — fetchCheckout() plus redirect decision.
   */
  async function checkoutAndRedirect(
    franquiaId: number,
  ): Promise<{ redirected: boolean; dados: SetupFee_CheckoutResponseDados }> {
    const dados = await fetchCheckout(franquiaId)

    if (dados?.checkout_url && isExternalCheckoutUrl(dados.checkout_url)) {
      redirectToCheckout(dados.checkout_url)
      return { redirected: true, dados }
    }

    return { redirected: false, dados }
  }


  function _reset(): void {
    checkingOutFranquiaId.value = null
    checkoutError.value = null
  }

  return {
    checkingOutFranquiaId,
    checkoutError,
    fetchCheckout,
    checkoutAndRedirect,
    redirectToCheckout,
    _reset,
  }
}
